"use client";

import { ROUTES } from "@/constants/routes";

export const CONTENT_MODAL_HISTORY_STATE_KEY = "hotpleContentModal";
export const CONTENT_MODAL_STORAGE_KEY = "hotpleContentModalContext";
export const CONTENT_MODAL_RUNTIME_KEY = "__hotpleContentModalContext";

const CONTENT_DETAIL_PATH_PATTERN = /^\/(?:post|play)\/[^/?#]+/;

export const isSafeInternalPath = (path) =>
  typeof path === "string" && path.startsWith("/") && !path.startsWith("//");

export const stripQueryAndHash = (path) =>
  typeof path === "string" ? path.split("?")[0].split("#")[0] : "";

export const getCurrentRelativePath = () => {
  if (typeof window === "undefined") return ROUTES.current.play;
  return `${window.location.pathname}${window.location.search || ""}`;
};

export const isContentDetailPath = (path) =>
  CONTENT_DETAIL_PATH_PATTERN.test(stripQueryAndHash(path));

const readStoredModalContext = () => {
  if (typeof window === "undefined") return null;

  try {
    const rawValue = window.sessionStorage?.getItem(CONTENT_MODAL_STORAGE_KEY);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch {
    return null;
  }
};

const writeStoredModalContext = (context) => {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage?.setItem(
      CONTENT_MODAL_STORAGE_KEY,
      JSON.stringify(context)
    );
  } catch {
    // Session storage can be unavailable in private browsing or strict settings.
  }
};

const removeStoredModalContext = () => {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage?.removeItem(CONTENT_MODAL_STORAGE_KEY);
  } catch {
    // Ignore storage failures. Runtime state is still cleared below.
  }
};

const extractPublicIdFromPath = (path) => {
  const [, , publicId = ""] = stripQueryAndHash(path).split("/");
  try {
    return decodeURIComponent(publicId);
  } catch {
    return publicId;
  }
};

const buildModalContext = ({
  canonicalPath,
  backgroundPath = ROUTES.current.play,
  source = "play_feed",
  contentKind = "post",
  content,
}) => {
  if (!isSafeInternalPath(canonicalPath)) return null;

  const currentPath = getCurrentRelativePath();
  const returnPath =
    isSafeInternalPath(currentPath) &&
    stripQueryAndHash(currentPath) !== stripQueryAndHash(canonicalPath)
      ? currentPath
      : backgroundPath;

  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    canonicalPath,
    backgroundPath,
    returnPath,
    renderMode: "modal",
    source,
    contentKind,
    contentId: content?.id || content?._id || "",
    publicId: content?.publicId || extractPublicIdFromPath(canonicalPath),
    savedAt: Date.now(),
  };
};

export const setActiveContentModalContext = (context) => {
  if (typeof window === "undefined" || !context) return null;

  window[CONTENT_MODAL_RUNTIME_KEY] = context;
  writeStoredModalContext(context);
  return context;
};

export const getHistoryContentModalContext = () => {
  if (typeof window === "undefined") return null;
  return window.history.state?.[CONTENT_MODAL_HISTORY_STATE_KEY] || null;
};

export const getActiveContentModalContext = () => {
  if (typeof window === "undefined") return null;

  const runtimeContext = window[CONTENT_MODAL_RUNTIME_KEY];
  const historyContext = getHistoryContentModalContext();

  if (!runtimeContext || !historyContext) return null;
  if (runtimeContext.id !== historyContext.id) return null;
  if (
    stripQueryAndHash(runtimeContext.canonicalPath) !==
    stripQueryAndHash(getCurrentRelativePath())
  ) {
    return null;
  }

  return runtimeContext;
};

export const clearActiveContentModalContext = () => {
  if (typeof window === "undefined") return;

  delete window[CONTENT_MODAL_RUNTIME_KEY];
  delete window.__hotplePlayModalActive;
  removeStoredModalContext();
};

export const openContentModalHistory = ({
  canonicalPath,
  backgroundPath = ROUTES.current.play,
  source = "play_feed",
  contentKind = "post",
  content,
}) => {
  if (typeof window === "undefined") return null;

  const context = buildModalContext({
    canonicalPath,
    backgroundPath,
    source,
    contentKind,
    content,
  });
  if (!context) return null;

  setActiveContentModalContext(context);
  window.history.pushState(
    {
      ...(window.history.state || {}),
      [CONTENT_MODAL_HISTORY_STATE_KEY]: context,
    },
    "",
    canonicalPath
  );

  return context;
};

export const getContentModalClosePath = ({
  currentPath = getCurrentRelativePath(),
  fallbackPath = ROUTES.current.play,
} = {}) => {
  const context =
    getActiveContentModalContext() ||
    getHistoryContentModalContext() ||
    readStoredModalContext();

  if (!context) return fallbackPath;

  const canonicalMatches =
    !context.canonicalPath ||
    stripQueryAndHash(context.canonicalPath) === stripQueryAndHash(currentPath);

  if (!canonicalMatches) return fallbackPath;
  if (isSafeInternalPath(context.returnPath)) return context.returnPath;
  if (isSafeInternalPath(context.backgroundPath)) return context.backgroundPath;
  return fallbackPath;
};

export const replaceContentModalHistoryOnClose = (closePath) => {
  if (typeof window === "undefined" || !isSafeInternalPath(closePath)) return;

  const historyState = { ...(window.history.state || {}) };
  delete historyState[CONTENT_MODAL_HISTORY_STATE_KEY];
  delete historyState.hotplePlayModal;

  window.history.replaceState(
    {
      ...historyState,
      hotpleContentModalClosed: true,
    },
    "",
    closePath
  );
};
